import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  isPlayBillingAvailable,
  restorePlayPurchases,
} from "@/lib/playBilling";
import { isRunningInTWA } from "@/lib/twa";

const BillingContext = createContext(null);

export const BillingProvider = ({ children }) => {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playBillingReady, setPlayBillingReady] = useState(false);

  const refresh = useCallback(async () => {
    // Fetch billing/me even for anonymous visitors — the backend handles
    // both auth modes and returns `{ is_anonymous: true, free_summary_limit:
    // 3, ... }` when only the X-Device-Id header is present.
    setLoading(true);
    try {
      const { data } = await api.get("/billing/me");
      setBilling(data);
      return data;
    } catch {
      setBilling(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Detect whether Google Play Billing (Digital Goods API) is available in
  // the current runtime. This is true only inside the TWA installed from Play.
  useEffect(() => {
    let cancelled = false;
    isPlayBillingAvailable().then((available) => {
      if (!cancelled) setPlayBillingReady(available);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // On login inside the TWA, reconcile any Play entitlement the backend may
  // have missed (reinstall, device swap, offline verification).
  useEffect(() => {
    if (!user || !playBillingReady) return;
    if (billing?.is_pro) return;
    const apiPost = (path, body) => {
      const token = localStorage.getItem("sux1_token");
      return fetch(`${api.defaults.baseURL}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
    };
    restorePlayPurchases(apiPost)
      .then((result) => {
        if (result.restored > 0) refresh();
      })
      .catch(() => {});
  }, [user, playBillingReady, billing?.is_pro, refresh]);

  const startCheckout = useCallback(async (packId) => {
    if (!packId) {
      window.location.assign("/billing/upgrade");
      return;
    }
    const { data } = await api.post("/billing/checkout", {
      package_id: packId,
      origin_url: window.location.origin,
      accepted_terms: true,
    });
    window.location.assign(data.url);
  }, []);

  const startBoosterCheckout = startCheckout;

  // Google Play policy 3.2 forbids non-Play checkouts inside a TWA: credit
  // packs are only sold on the web, so the TWA always shows the web notice.
  const isTWA = useMemo(() => isRunningInTWA(), []);

  const paymentsBlockedInTWA = isTWA;

  const credits = billing?.credits || 0;
  const unlimitedActive = !!billing?.is_unlimited && (billing.unlimited_used || 0) < (billing.unlimited_cap || 100);
  const freeAccess = !!billing?.is_admin || unlimitedActive;
  const canUseExtras = freeAccess || !!billing?.is_pro || credits > 0;
  const isLocked =
    !!billing && !freeAccess && !billing.is_pro && credits === 0 &&
    billing.free_summaries_used >= billing.free_summary_limit;
  const remaining = billing
    ? Math.max(0, billing.free_summary_limit - billing.free_summaries_used)
    : null;

  // Pro monthly quota derived state (Phase 1 — Oct 2026).
  // `monthlyRemaining` = remaining in the current month's bucket (0..quota).
  // `bonusRemaining`   = top-up balance from booster packs.
  // `isMonthlyLocked`  = Pro has blown the monthly cap AND has no boosters.
  //                      When true, the Dashboard shows the booster paywall.
  const monthlyRemaining = billing?.is_pro
    ? Math.max(0, (billing.monthly_quota || 20) - (billing.monthly_summaries_used || 0))
    : null;
  const bonusRemaining = billing?.bonus_summaries || 0;
  const isMonthlyLocked = !!(
    !freeAccess &&
    billing?.is_pro &&
    monthlyRemaining === 0 &&
    bonusRemaining === 0
  );

  return (
    <BillingContext.Provider
      value={{
        billing,
        freeAccess,
        canUseExtras,
        unlimitedActive,
        loading,
        refresh,
        startCheckout,
        startBoosterCheckout,
        isLocked,
        isMonthlyLocked,
        credits,
        remaining,
        monthlyRemaining,
        bonusRemaining,
        playBillingReady,
        isTWA,
        paymentsBlockedInTWA,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used inside BillingProvider");
  return ctx;
};
